import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaWhatsapp, FaPhone, FaEnvelope } from 'react-icons/fa';

const ContactPage = () => {
  const whatsappURL = `https://wa.me/2348024980215?text=Hello%20KambiHome%2C%20I%20would%20like%20to%20make%20an%20inquiry.`;
  
  return (
    <>
      <Helmet>
        <title>Contact KambiHome | Shopping, Sourcing & Procurement Support</title>
        <meta
          name="description"
          content="Contact KambiHome for shopping support, business sourcing, procurement enquiries, supply partnerships, and general assistance."
        />
        <meta property="og:title" content="Contact KambiHome" />
        <meta
          property="og:description"
          content="Reach KambiHome for consumer support, business sourcing, procurement, and supply partnership enquiries."
        />
        <meta property="og:url" content="https://kambihome.com/contact" />
      </Helmet>

      <Header />

      <section className="bg-gradient-to-br from-green-100 to-white py-16 text-center px-6 mt-16">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-green-700 mb-3">
          Shopping • Sourcing • Procurement
        </p>
        <h1 className="text-4xl font-bold text-green-700 mb-4 animate-fade-in">Contact Us</h1>
        <p className="text-gray-700 text-lg max-w-3xl mx-auto leading-relaxed">
          Contact KambiHome for product support, business sourcing requirements, procurement enquiries, or supply partnership opportunities.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-10">
        <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-lg">
          <h2 className="text-2xl font-semibold text-green-700 mb-4">Contact Information</h2>

          <div className="space-y-3 text-gray-700">
            <p className="flex items-center gap-3"><FaPhone /> <strong>Phone (NG):</strong> +2348024980215</p>
            <p className="flex items-center gap-3"><FaEnvelope /> <strong>Email:</strong> talktous@kambihome.com</p>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Office Address</h3>
            <p>35 Igando-Ikotun Road (Suite 19, Praise Plaza), opposite Fidelity Bank Igando Lagos</p>
          </div>

          <div className="mt-6 rounded-lg bg-green-50 border border-green-100 p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Business Sourcing & Supply Partnerships</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Retailers, SMEs, hospitality businesses, interior professionals, property businesses, commercial buyers, suppliers, and brands can contact our team to discuss sourcing and partnership requirements.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <a
            href={whatsappURL}
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer bg-green-600 hover:bg-green-700 hover:scale-[1.02] transition-all duration-300 text-white px-6 py-4 rounded-lg flex items-center justify-center text-lg font-semibold shadow-md"
          >
            <FaWhatsapp className="mr-2 text-2xl" />
            Chat with us on WhatsApp
          </a>

          <div className="rounded-xl overflow-hidden shadow-md transition-all duration-300 hover:shadow-lg">
            <iframe
              title="KambiHome Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.7711159030696!2d3.238070567845016!3d6.550556308384226!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b854167fa6a9d%3A0xd166786c61663c78!2s19%2C%20H62V%2B636%2C%2035%20Igando%20Rd%2C%20Ikotun%2C%20Lagos%20102213%2C%20Lagos!5e0!3m2!1sen!2sng!4v1768219936104!5m2!1sen!2sng" 
              width="100%"
              height="350"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full border-0"
            ></iframe>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-8">
            Frequently Asked Questions
          </h2>
          <FAQAccordion
            faqs={[
              {
                question: "Do you support delivery to African markets?",
                answer:
                  "KambiHome provides access to products through connected supply corridors. Available shipping and fulfilment options depend on the product source, destination, and selected delivery option.",
              },
              {
                question: "Can KambiHome help my business source products?",
                answer:
                  "Yes. Businesses can contact KambiHome with sourcing or procurement requirements for products, suppliers, wholesale opportunities, and commercial purchasing needs.",
              },
              {
                question: "Can suppliers and brands work with KambiHome?",
                answer:
                  "Yes. Suppliers, manufacturers, wholesalers, and brands can contact our team to discuss potential supply corridor and partnership opportunities.",
              },
              {
                question: "Can I contact support outside business hours?",
                answer:
                  "You can send us a message on WhatsApp or email at any time, and our team will respond as soon as possible.",
              },
              {
                question: "Where is KambiHome located?",
                answer:
                  "Our listed business address is at 35 Igando-Ikotun Road, Suite 19, Praise Plaza, opposite Fidelity Bank, Igando, Lagos, Nigeria.",
              },
            ]}
          />
        </div>
      </section>

      <Footer />
    </>
  );
};

const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="bg-white rounded-md shadow-md transition hover:shadow-lg"
        >
          <button
            className="cursor-pointer w-full text-left p-4 flex justify-between items-center focus:outline-none"
            onClick={() => toggle(index)}
          >
            <span className="font-semibold text-gray-800">{faq.question}</span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                openIndex === index ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              openIndex === index ? "max-h-40 p-4 pt-0" : "max-h-0 px-4"
            }`}
          >
            <p className="text-gray-600">{faq.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContactPage;
